import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import LocalPharmacyOutlined from '@mui/icons-material/LocalPharmacyOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { capitalize } from 'lodash';
import { format, isValid } from 'date-fns';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { markCentralFillOrderDelivered } from 'store/thunks/workQueueThunks';

const NOTE_MAX_LENGTH = 250;

const SummaryRow = ({ icon, label, children }) => (
  <Stack direction='row' alignItems='center' spacing={1.5}>
    <Box sx={{ display: 'flex', color: 'primary.main' }}>{icon}</Box>
    <Typography variant='body2' color='text.secondary' sx={{ width: 170, flexShrink: 0 }}>
      {label}
    </Typography>
    {children}
  </Stack>
);

SummaryRow.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const FieldRow = ({ label, children }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}>
    <Typography variant='body2' fontWeight={600} sx={{ width: 170, flexShrink: 0, pt: 1 }}>
      {label}{' '}
      <Box component='span' sx={{ color: 'error.main' }}>
        *
      </Box>
    </Typography>
    <Box sx={{ flexGrow: 1 }}>{children}</Box>
  </Stack>
);

FieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const MarkCentralFillOrderDeliveredDialog = ({
  open,
  onClose,
  trackingId,
  orderId,
  patientName,
  drugName,
  compoundingPharmacyName,
  status,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [reason, setReason] = useState('');
  const [deliveredDate, setDeliveredDate] = useState(new Date());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setDeliveredDate(new Date());
    }
  }, [open]);

  const trimmedReason = reason.trim();
  const isDateValid = isValid(deliveredDate);
  const canSubmit = !!trimmedReason && isDateValid && !submitting;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      await dispatch(
        markCentralFillOrderDelivered({
          trackingId,
          orderId,
          reason: trimmedReason,
          deliveredDate: format(deliveredDate, 'yyyy-MM-dd'),
        })
      );
      enqueueSnackbar('Order marked as delivered.', { variant: 'success' });
      onClose();
    } catch {
      // Error already surfaced via handleError in the thunk; keep the dialog
      // open so the note and date survive for a retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='mark-delivered-dialog-title'
      fullWidth
      maxWidth='sm'
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Stack direction='row' alignItems='flex-start' spacing={2} sx={{ p: 3, pb: 2 }}>
        <Box
          sx={{
            bgcolor: 'success.main',
            color: 'common.white',
            borderRadius: 1.5,
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <TaskAltOutlined />
        </Box>

        <Box sx={{ flexGrow: 1 }}>
          <Typography id='mark-delivered-dialog-title' variant='h6' fontWeight={600}>
            Mark Central Fill Order Delivered
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Use this when the central fill pharmacy delivered the order but the delivered update
            never reached PerfectRx.
          </Typography>
        </Box>

        <IconButton aria-label='close' onClick={handleClose} disabled={submitting} size='small'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Stack>

      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={2.5}>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'grey.50',
              px: 2.5,
              py: 2,
            }}
          >
            <Stack spacing={1.5}>
              <SummaryRow icon={<DescriptionOutlined fontSize='small' />} label='Order'>
                <Typography variant='body2' fontWeight={600}>
                  {orderId}
                </Typography>
              </SummaryRow>

              {patientName && (
                <SummaryRow icon={<PersonOutlined fontSize='small' />} label='Patient'>
                  <Typography variant='body2'>{patientName}</Typography>
                </SummaryRow>
              )}

              {drugName && (
                <SummaryRow icon={<LocalPharmacyOutlined fontSize='small' />} label='Drug'>
                  <Typography variant='body2'>{drugName}</Typography>
                </SummaryRow>
              )}

              {compoundingPharmacyName && (
                <SummaryRow
                  icon={<ApartmentOutlined fontSize='small' />}
                  label='Compounding pharmacy'
                >
                  <Typography variant='body2'>{compoundingPharmacyName}</Typography>
                </SummaryRow>
              )}

              {status && (
                <SummaryRow icon={<CheckCircleOutlined fontSize='small' />} label='Current status'>
                  <Chip
                    label={capitalize(status.toLowerCase().replace(/_/g, ' '))}
                    size='small'
                    variant='outlined'
                  />
                </SummaryRow>
              )}
            </Stack>
          </Box>

          <Divider />

          <FieldRow label='Delivered date'>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                value={deliveredDate}
                onChange={(date) => setDeliveredDate(date)}
                disableFuture
                maxDate={new Date()}
                renderInput={({
                  inputRef,
                  InputProps: inputComponentProps,
                  inputProps: nativeInputProps,
                  disabled,
                  error,
                }) => (
                  <TextField
                    inputRef={inputRef}
                    InputProps={{
                      ...inputComponentProps,
                      inputProps: {
                        ...nativeInputProps,
                        'aria-label': 'Delivered date',
                      },
                    }}
                    disabled={disabled}
                    error={error || !isDateValid}
                    helperText={
                      isDateValid
                        ? 'Select the date the order was delivered by the pharmacy.'
                        : 'Enter a valid delivered date'
                    }
                    FormHelperTextProps={{ sx: { fontSize: 10, ml: 0.5, mr: 0 } }}
                    size='small'
                    fullWidth
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem' } }}
                  />
                )}
              />
            </LocalizationProvider>
          </FieldRow>

          <FieldRow label='Note'>
            <TextField
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder='Add a note'
              inputProps={{ 'aria-label': 'mark delivered note', maxLength: NOTE_MAX_LENGTH }}
              helperText={`${reason.length} / ${NOTE_MAX_LENGTH}`}
              FormHelperTextProps={{ sx: { fontSize: 12, mx: 0, textAlign: 'right' } }}
              multiline
              minRows={2}
              maxRows={4}
              size='small'
              fullWidth
            />
          </FieldRow>

          <Alert severity='info'>
            The order follows the same workflow as a delivered update from the pharmacy.
          </Alert>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant='outlined' color='secondary' onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          color='primary'
          loading={submitting}
          disabled={!canSubmit}
          onClick={handleConfirm}
        >
          Mark as Delivered
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

MarkCentralFillOrderDeliveredDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trackingId: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  patientName: PropTypes.string,
  drugName: PropTypes.string,
  compoundingPharmacyName: PropTypes.string,
  status: PropTypes.string,
};

MarkCentralFillOrderDeliveredDialog.defaultProps = {
  patientName: undefined,
  drugName: undefined,
  compoundingPharmacyName: undefined,
  status: undefined,
};

export default MarkCentralFillOrderDeliveredDialog;
